/* eslint-disable no-empty */
import React from 'react';
import axios, { AxiosError } from 'axios';
import { apiUrl } from 'utils/apiUrl';
import { ClientData } from 'App.types';
import { useAuthContext } from 'contexts/AuthContext';

interface ClientsResponse {
  items: ClientData[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  isLastPage: boolean;
}

export interface ClientsContextProps {
  clientsMap: Map<string, ClientData>;
  loading: boolean;
  error?: Error | null;
  refresh: () => Promise<void>;
}

const TTL_MS = 2 * 60 * 60 * 1000;
const MAX_RETRIES = 3;

const PAGE_KEY = (page: number) => `clients:p:${page}`;
const META_KEY = 'clients:meta';

const delay = (ms: number) =>
  new Promise((r) => {
    setTimeout(r, ms);
  });

let derivedKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey> {
  if (derivedKey) return derivedKey;

  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    'raw',
    enc.encode('session-secret'),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: enc.encode('clients-cache-salt'),
      iterations: 80000,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );

  return derivedKey;
}

async function encryptData(data: unknown): Promise<string> {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

  return JSON.stringify({
    iv: Array.from(iv),
    ct: Array.from(new Uint8Array(ciphertext)),
  });
}

async function decryptData(encrypted: string): Promise<any | null> {
  try {
    const key = await getKey();
    const { iv, ct } = JSON.parse(encrypted);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(iv) },
      key,
      new Uint8Array(ct),
    );

    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch {
    return null;
  }
}

async function savePage(page: number, items: ClientData[]) {
  try {
    const encrypted = await encryptData({
      exp: Date.now() + TTL_MS,
      items,
    });
    sessionStorage.setItem(PAGE_KEY(page), encrypted);
  } catch {}
}

async function loadPage(page: number): Promise<ClientData[] | null> {
  const raw = sessionStorage.getItem(PAGE_KEY(page));
  if (!raw) return null;

  const decrypted = await decryptData(raw);
  if (!decrypted) return null;
  if (Date.now() > decrypted.exp) return null;

  return decrypted.items as ClientData[];
}

async function saveMeta(totalPages: number) {
  try {
    const encrypted = await encryptData({
      exp: Date.now() + TTL_MS,
      totalPages,
    });
    sessionStorage.setItem(META_KEY, encrypted);
  } catch {}
}

async function loadMeta(): Promise<number | null> {
  const raw = sessionStorage.getItem(META_KEY);
  if (!raw) return null;

  const decrypted = await decryptData(raw);
  if (!decrypted) return null;
  if (Date.now() > decrypted.exp) return null;

  return decrypted.totalPages as number;
}

async function fetchPage(idToken: string, page: number, attempt = 1): Promise<ClientsResponse> {
  const cached = await loadPage(page);
  if (cached) {
    return {
      items: cached,
      page,
      pageSize: cached.length,
      totalItems: 0,
      totalPages: (await loadMeta()) ?? page,
      isLastPage: false,
    };
  }

  try {
    const r = await axios.get<ClientsResponse>(apiUrl('tenancy', `clients?page=${page}`), {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    await savePage(page, r.data.items);
    await saveMeta(r.data.totalPages);

    return r.data;
  } catch (err) {
    const axiosError = err as AxiosError;
    const status = axiosError.response?.status;
    const retryable = !status || (status >= 500 && status < 600);

    if (attempt < MAX_RETRIES && retryable) {
      await delay(300 * attempt);
      return fetchPage(idToken, page, attempt + 1);
    }

    throw err as Error;
  }
}

async function fetchAllClientsPaged(idToken: string): Promise<Map<string, ClientData>> {
  const acc = new Map<string, ClientData>();

  const first = await fetchPage(idToken, 1);
  first.items.forEach((c) => acc.set(c.tenantId, c));

  const totalPages = (await loadMeta()) ?? first.totalPages;
  const pages = Array.from({ length: Math.max(totalPages - 1, 0) }, (_, i) => i + 2);

  await pages.reduce(async (p, page) => {
    await p;
    const res = await fetchPage(idToken, page);
    res.items.forEach((c) => acc.set(c.tenantId, c));
  }, Promise.resolve());

  return acc;
}

export const ClientsContext = React.createContext<ClientsContextProps>({
  clientsMap: new Map(),
  loading: true,
  error: null,
  refresh: async () => {},
});

export const useClientsContext = () => React.useContext(ClientsContext);

export const ClientsContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { idToken, loading: authLoading } = useAuthContext();

  const [clientsMap, setClientsMap] = React.useState<Map<string, ClientData>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  const refresh = React.useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const all = await fetchAllClientsPaged(idToken);
      setClientsMap(all);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  React.useEffect(() => {
    let cancelled = false;

    async function init() {
      if (authLoading) return;

      if (!idToken) {
        setClientsMap(new Map());
        setLoading(false);
        return;
      }

      try {
        const all = await fetchAllClientsPaged(idToken);
        if (!cancelled) setClientsMap(all);
      } catch (e) {
        if (!cancelled) setError(e as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [idToken, authLoading]);

  const value = React.useMemo(
    () => ({ clientsMap, loading, error, refresh }),
    [clientsMap, loading, error, refresh],
  );

  return <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>;
};

export default ClientsContextProvider;
